/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from './Popup_Help';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import {
  getCustomChildItemTypes,
  getManager,
  getName,
} from '../../registry/Manager';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { defaultValue } from './Popup';

const name = 'Popup';

const PopupManager: ComponentManagerFn<'Popup'> = (
  platform: Platform,
): ComponentManager<'Popup'> => {
  return {
    ...baseComponentManager<'Popup'>(platform, 'Popup'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) => {
      if (
        !(
          [
            ...getCustomChildItemTypes('Popup'),
            'CanvasGrid',
            'Card',
            'Chartjs',
            'Code',
            'CombinationChart',
            'DataGrid',
            'DataStoreReference',
            'FlexPanel',
            'Form',
            'FunctionEditor',
            'HorizontalSplitPanel',
            'Mirror',
            'PopupActions',
            'RowPanel',
            'SearchForm',
            'TabPanel',
            'Toolbar',
            'TriggerOnPopupClose',
            'TriggerOnPopupOpen',
            'VerticalSplitPanel',
            'WorkflowViewer',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (childType === 'PopupActions') {
        const children = platform.getChildrenOfType(
          context.appUid,
          context.pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      if (childType === 'Toolbar') {
        const children = platform.getChildrenOfType(
          context.appUid,
          context.pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      return checkForFixedChild(platform, page, item, childType, name, context);
    },
    onChildMoveIn: ({ page, value }) => {
      // PopupActions must be the last child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'PopupActions') {
            return 1;
          }
          if (bItem.type === 'PopupActions') {
            return -1;
          }
          if (aItem.type === 'Toolbar') {
            return -1;
          }
          if (bItem.type === 'Toolbar') {
            return 1;
          }
        }
        return 0;
      });
    },
    props: {
      hideHeader: {
        label: 'Hide Header',
        type: 'boolean',
      },
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      modal: {
        label: 'Modal',
        type: 'boolean',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      isEmbedded: {
        label: 'Is Embedded',
        type: 'boolean',
        helpText: 'Check this if this popup is in loop',
      },
      fullScreen: {
        label: 'Full Screen',
        type: 'boolean',
      },
      bodyPadding: {
        label: 'Body Padding',
        type: 'boolean',
        category: 'Spacing',
      },
      popupWidth: {
        label: 'Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      left: {
        label: 'Left',
        type: 'number',
        hidden: true,
      },
      top: {
        label: 'Top',
        type: 'number',
        hidden: true,
      },
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'sizeAware',
    isPropertyVisible(prop, { hideHeader }): boolean {
      switch (prop) {
        case 'left':
        case 'top':
          return false;
        case 'title':
          return !hideHeader;
        default:
          return true;
      }
    },
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isItemIdUsed(itm, item.itemId);
      });
      return usage.length
        ? `${item.props.title.value} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.title.value;
    },
    icon: { iconName: 'window-restore' },
    generateShortId: (item) =>
      item.title?.type === 'text' ? item.title.value : '',
    description:
      'Popup is a small window that is displayed on top of the current page. It is used to display the information e.g. contact information, address information etc.',
    helpRenders: [
      {
        title: 'Popup Size',
        description:
          "Change the props and press 'Open Popup' button to see your changes in action.",
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: false,
        visibleProps: ['fullScreen', 'popupHeight', 'popupWidth', 'title'],
        item: {
          children: ['popupActions'],
          itemId: 'popup',
          parent: 'root',
          props: {
            title: {
              type: 'text',
              value: 'Awesome Title',
            },
          },
          type: 'Popup',
        },
      },
      {
        title: 'Model Window',
        description:
          'When the Model prop is on, the user has to close the popup explicitly. Clicking outside the popup area will not automatically close it.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: false,
        visibleProps: ['modal'],
        item: {
          children: ['popupActions'],
          itemId: 'popup',
          parent: 'root',
          props: {
            title: {
              type: 'text',
              value: 'Awesome Title',
            },
            modal: true,
          },
          type: 'Popup',
        },
      },
    ],
  };
};

export default PopupManager;
